import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Birthday } from '@domain/entities/Birthday';
import { initialsOf } from '@domain/entities/Person';
import {
  birthdaysInMonth,
  birthdaysOn,
  celebrationDay,
  nextBirthdays,
} from '@domain/policies/BirthdayPolicy';
import { getServices } from '@infrastructure/services';
import {
  Banner,
  Button,
  Card,
  EmptyState,
  MonthSwitcher,
  Spinner,
  cssVars,
  cx,
} from '../../design-system';
import { MONTHS_LONG, pluralize, reasonFor } from '../../format';
import { CakeIcon } from './CakeIcon';
import { Confetti } from './Confetti';
import { sizedPhoto } from './photo';
import styles from './BlogScreen.module.css';

/**
 * Cumpleaños — la primera pestaña de Kn·Blog.
 *
 * Los de hoy van primero y en grande, como el cartel que RR.HH. manda cada
 * semana; debajo, los del mes, que se puede recorrer. Al entrar cae confeti.
 *
 * Cada mes es un documento ya armado en el servidor, sin año: hoy y el mes en
 * curso salen del mismo, y cambiar de mes es leer otro.
 */

const WEEKDAYS_LONG = [
  'domingo',
  'lunes',
  'martes',
  'miércoles',
  'jueves',
  'viernes',
  'sábado',
] as const;

/** Las seis de `tokens.css`, en el orden en que se reparten. */
const TILE_COLORS = [
  'var(--blog-tile-1)',
  'var(--blog-tile-2)',
  'var(--blog-tile-3)',
  'var(--blog-tile-4)',
  'var(--blog-tile-5)',
  'var(--blog-tile-6)',
] as const;

/**
 * El color de cada tarjeta, por su lugar en la rejilla y no por la persona.
 *
 * Por persona —un hash del id— dos vecinas se pintaban igual casi la mitad de
 * las veces con sólo tres cumpleañeros, y el cartel que se imita alterna. El
 * precio es que alguien puede salir de un color en «hoy» y de otro en el mes.
 */
const tileColor = (index: number): string => TILE_COLORS[index % TILE_COLORS.length] ?? TILE_COLORS[0];

/** Lo que la tarjeta pide a Google: el doble de lo que mide, para pantallas retina. */
const PHOTO_PX = 400;

const monthName = (month: number): string => MONTHS_LONG[month - 1] ?? '';

/**
 * Lo más que se esperan las fotos de hoy antes de soltar el confeti y el mes.
 *
 * Una foto que no llega —red lenta, Google que tarda— no puede dejar la pantalla
 * sin fiesta: pasado esto, se celebra con lo que haya, y la foto entra cuando
 * llegue.
 */
const HERO_PHOTO_WAIT_MS = 2500;

/** Un mes de cumpleaños. Hoy y el mes en curso comparten este mismo documento. */
const monthQuery = (month: number) => ({
  queryKey: ['blog', 'cumpleanos', month] as const,
  queryFn: async () => {
    const result = await getServices().birthdays.listMonth(month);
    if (!result.ok) {
      throw new Error(`No pudimos cargar los cumpleaños.${reasonFor(result.error)}`);
    }
    return result.value;
  },
  /* Cambian una vez al año por persona: media hora sin volver a preguntar es nada. */
  staleTime: 30 * 60_000,
});

interface TileProps {
  readonly birthday: Birthday;
  readonly index: number;
  readonly year: number;
  readonly isToday: boolean;
  readonly size: 'hero' | 'compact';
  /** Las de hoy: su foto se pide ya y antes que cualquier otra cosa. */
  readonly priority?: boolean;
  /** Avisa cuando la foto llegó —o falló—: es lo que suelta el confeti. */
  readonly onPhotoSettled?: (personId: string) => void;
}

const BirthdayTile = ({
  birthday,
  index,
  year,
  isToday,
  size,
  priority = false,
  onPhotoSettled,
}: TileProps): ReactNode => {
  const day = celebrationDay(birthday, year);
  const settle = (): void => {
    onPhotoSettled?.(birthday.personId);
  };
  return (
    <li
      className={cx(styles.tile, size === 'hero' ? styles.tileHero : styles.tileCompact)}
      style={cssVars({ '--tile-color': tileColor(index) })}
    >
      <div className={styles.tileText}>
        <h3 className={styles.firstName}>{birthday.firstName}</h3>
        <p className={styles.fullName}>{birthday.displayName}</p>
        {birthday.areaName.length > 0 && <p className={styles.areaName}>{birthday.areaName}</p>}
      </div>

      <p className={styles.date}>
        <span className={styles.dateDay}>{day}</span>
        <span className={styles.dateMonth}>{monthName(birthday.month)}</span>
      </p>

      <div className={styles.portrait} aria-hidden="true">
        {birthday.photoURL !== null ? (
          <img
            src={sizedPhoto(birthday.photoURL, PHOTO_PX)}
            alt=""
            className={styles.photo}
            loading={priority ? 'eager' : 'lazy'}
            fetchPriority={priority ? 'high' : 'low'}
            decoding="async"
            onLoad={settle}
            onError={settle}
            /* Google responde 403 a algunas fotos si viaja el origen. */
            referrerPolicy="no-referrer"
          />
        ) : (
          <span className={styles.initials}>{initialsOf(birthday.displayName)}</span>
        )}
      </div>

      {isToday && <span className={styles.todayBadge}>🎂 Hoy</span>}
    </li>
  );
};

/**
 * En tres tiempos, y en este orden, porque así se pidió el 1 oct 2026: «las que
 * tenemos que asegurar son las de hoy».
 *
 * 1. **Los de hoy.** El documento del mes en curso —uno, pequeño— y sus fotos,
 *    pedidas antes que nada.
 * 2. **El confeti**, cuando esas fotos ya están (o pasado `HERO_PHOTO_WAIT_MS`).
 *    Antes caía al entrar, sobre una pantalla todavía vacía.
 * 3. **El resto del mes**, después: sus tarjetas se pintan hasta entonces y sus
 *    fotos se piden con prioridad baja, sin competir con las de hoy.
 */
export const BirthdaysTab = (): ReactNode => {
  const today = getServices().clock.today();
  const [month, setMonth] = useState(today);
  /* Cambia y cae otra ráfaga. La primera es la de llegar. */
  const [burst, setBurst] = useState(0);

  // --- 1 · Los de hoy -------------------------------------------------------
  const current = useQuery(monthQuery(today.month));
  const todays = useMemo(() => birthdaysOn(current.data ?? [], today), [current.data, today]);

  const heroPhotoIds = useMemo(
    () => todays.filter((b) => b.photoURL !== null).map((b) => b.personId),
    [todays],
  );
  const [settled, setSettled] = useState<ReadonlySet<string>>(() => new Set());
  const [gaveUp, setGaveUp] = useState(false);
  const markSettled = useCallback((personId: string) => {
    setSettled((prev) => (prev.has(personId) ? prev : new Set([...prev, personId])));
  }, []);
  useEffect(() => {
    if (!current.isSuccess) return;
    const timer = window.setTimeout(() => {
      setGaveUp(true);
    }, HERO_PHOTO_WAIT_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [current.isSuccess]);

  // --- 2 · Listo para celebrar ----------------------------------------------
  const heroReady =
    current.isSuccess && (gaveUp || heroPhotoIds.every((personId) => settled.has(personId)));

  /*
   * El próximo, cuando hoy no cumple nadie. Casi siempre está en este mismo mes;
   * sólo si no queda nadie después de hoy se lee el siguiente.
   */
  const laterThisMonth = (current.data ?? []).some(
    (b) => celebrationDay(b, today.year) > today.day,
  );
  const needsFollowing = current.isSuccess && todays.length === 0 && !laterThisMonth;
  const following = useQuery({
    ...monthQuery(today.month === 12 ? 1 : today.month + 1),
    enabled: needsFollowing,
  });
  const next = useMemo(() => {
    if (!current.isSuccess || todays.length > 0) return null;
    if (needsFollowing && !following.isSuccess) return null;
    return nextBirthdays([...current.data, ...(following.data ?? [])], today);
  }, [current.isSuccess, current.data, todays.length, needsFollowing, following.isSuccess, following.data, today]);

  // --- 3 · El resto del mes -------------------------------------------------
  const viewed = useQuery({ ...monthQuery(month.month), enabled: heroReady });
  /*
   * Detrás de `heroReady` también aquí, y no sólo en la consulta: el mes en curso
   * es el mismo documento que «hoy», así que ya está en caché y `enabled: false`
   * no lo esconde. Sin esto la rejilla entera se pintaba antes que las fotos de
   * hoy, que es justo lo que se quería evitar.
   */
  const ofMonth = useMemo(
    () => (heroReady ? birthdaysInMonth(viewed.data ?? [], month.year, month.month) : []),
    [heroReady, viewed.data, month.year, month.month],
  );
  const monthShown = heroReady && viewed.isSuccess;

  const showingThisMonth = month.year === today.year && month.month === today.month;
  const todayLabel = `${WEEKDAYS_LONG[today.dayOfWeek] ?? ''} ${String(today.day)} de ${monthName(
    today.month,
  )}`;

  return (
    <div className={styles.birthdays}>
      {heroReady && <Confetti burst={burst} />}

      <section className={styles.hero} aria-labelledby="blog-hoy">
        <div className={styles.heroHead}>
          <CakeIcon className={styles.cake} />
          <div className={styles.heroCopy}>
            <h2 id="blog-hoy" className={styles.heroTitle}>
              ¡Feliz cumpleaños!
            </h2>
            <p className={styles.heroNote}>
              {current.isPending
                ? `Hoy, ${todayLabel}.`
                : todays.length > 0
                  ? `Hoy, ${todayLabel}, celebramos a ${pluralize(todays.length, 'persona', 'personas')}.`
                  : `Hoy, ${todayLabel}, no cumple nadie.`}
            </p>
          </div>
          <Button
            variant="ghost"
            disabled={!heroReady}
            onClick={() => {
              setBurst((n) => n + 1);
            }}
          >
            🎉 Más confeti
          </Button>
        </div>

        {current.isPending && <Spinner label="Buscando quién cumple hoy…" />}
        {current.isError && <Banner tone="danger">{current.error.message}</Banner>}

        {todays.length > 0 && (
          <ul className={styles.heroGrid}>
            {todays.map((birthday, index) => (
              <BirthdayTile
                key={birthday.personId}
                birthday={birthday}
                index={index}
                year={today.year}
                isToday
                size="hero"
                priority
                onPhotoSettled={markSettled}
              />
            ))}
          </ul>
        )}

        {next !== null && (
          <p className={styles.next}>
            El próximo {next.people.length === 1 ? 'cumpleaños es el de' : 'día de pastel es para'}{' '}
            <strong>{next.people.map((b) => b.firstName).join(', ')}</strong>, el{' '}
            {next.date.day} de {monthName(next.date.month)}.
          </p>
        )}
      </section>

      {!current.isError && (
        <Card>
          <div className={styles.monthHead}>
            <h2 className={styles.monthTitle}>
              Cumpleaños de {monthName(month.month)}
              {monthShown && <span className={styles.monthCount}>{ofMonth.length}</span>}
            </h2>
            <MonthSwitcher month={month} today={today} onChange={setMonth} label="Mes de cumpleaños" />
          </div>

          {(!heroReady || viewed.isPending) && <Spinner label="Cargando el resto del mes…" />}
          {viewed.isError && <Banner tone="danger">{viewed.error.message}</Banner>}

          {monthShown && ofMonth.length === 0 && (
            <EmptyState glyph="🎈" title={`Nadie cumple en ${monthName(month.month)}.`} />
          )}

          {ofMonth.length > 0 && (
            <ul className={styles.monthGrid}>
              {ofMonth.map((birthday, index) => (
                <BirthdayTile
                  key={birthday.personId}
                  birthday={birthday}
                  index={index}
                  year={month.year}
                  isToday={showingThisMonth && celebrationDay(birthday, month.year) === today.day}
                  size="compact"
                />
              ))}
            </ul>
          )}
        </Card>
      )}
    </div>
  );
};
